import React, { memo, useCallback } from 'react';

import { ThreeDStockLifestylesListItemLifestyle } from './ThreeDStockLifestylesListItem.types';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import {
  LifestyleNanoID,
  OnLifestyleSimilarSearchAction,
  UpdateLifestyleCacheAction
} from '../../../../lifestyles/lifestylesTypes';
import { OnLightboxOpenAction } from '../../../../../helpers/LightboxWrapper';
import {
  FetchLifestylesSetsCacheKeys,
  UpdateLifestylesSetCacheAction
} from '../../../../lifestylesSets/lifestylesSetsTypes';
import { ThreeDStockLifestylesListItemInfoLifestylesSet } from './components/ThreeDStockLifestylesListItemInfo/ThreeDStockLifestylesListItemInfo.types';

import { ThreeDStockLifestylesListItemPreview } from './components/ThreeDStockLifestylesListItemPreview';
import { ThreeDStockLifestylesListItemInfo } from './components/ThreeDStockLifestylesListItemInfo';

import { TeamPath } from '../../../../teams/TeamPath';
import { LifestylesPermissions } from '../../../../lifestyles/lifestylesConstants';
import { LifestylePath } from '../../../../lifestyles/LifestylePath';

type ThreeDStockLifestylesListItemProps = {
  lifestyle: ThreeDStockLifestylesListItemLifestyle;
  updateLifestyleCache?: UpdateLifestyleCacheAction<ThreeDStockLifestylesListItemLifestyle>;
  onLifestyleEditButtonMouseEnter?: (lifestyleNanoId: LifestyleNanoID) => void;
  onLightboxOpen: OnLightboxOpenAction;
  lifestylesSet: ThreeDStockLifestylesListItemInfoLifestylesSet | null;
  lifestylesSetCacheKeys?: FetchLifestylesSetsCacheKeys;
  updateLifestylesSetCache: UpdateLifestylesSetCacheAction<ThreeDStockLifestylesListItemInfoLifestylesSet>;
  onLifestyleSimilarSearchFilter?: OnLifestyleSimilarSearchAction;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  withRealAspectRatio?: boolean;
};

function ThreeDStockLifestylesListItem({
  lifestyle,
  updateLifestyleCache,
  onLifestyleEditButtonMouseEnter,
  onLightboxOpen,
  lifestylesSet,
  updateLifestylesSetCache,
  lifestylesSetCacheKeys,
  companyNanoId,
  isMyLibrary,
  withRealAspectRatio,
  onLifestyleSimilarSearchFilter
}: ThreeDStockLifestylesListItemProps) {
  const handleOpenLightbox = useCallback(() => {
    if (lifestyle.image) {
      onLightboxOpen(lifestyle);
    }
  }, [lifestyle, onLightboxOpen]);

  const handleEditButtonMouseEnter = useCallback<() => void>(() => {
    onLifestyleEditButtonMouseEnter(lifestyle.nanoId);
  }, [onLifestyleEditButtonMouseEnter, lifestyle.nanoId]);

  let downloadButtonAction =
    LifestylesPermissions.READ_LIFESTYLE_DOWNLOAD_BUTTON;
  let downloadSeparatelyButtonAction =
    LifestylesPermissions.READ_LIFESTYLE_DOWNLOAD_SEPARATELY_BUTTON;
  let lifestylePagePath = LifestylePath.show(lifestyle.nanoId);
  let lifestyleEditPagePath = LifestylePath.edit(lifestyle.nanoId);

  if (isMyLibrary) {
    lifestylePagePath = TeamPath.currentCompanyNewLibraryLifestyle(
      lifestyle.nanoId
    );
    downloadButtonAction =
      LifestylesPermissions.READ_SELF_COMPANY_LIBRARY_LIFESTYLE_DOWNLOAD_BUTTON;
    downloadSeparatelyButtonAction =
      LifestylesPermissions.READ_SELF_COMPANY_LIBRARY_LIFESTYLE_DOWNLOAD_SEPARATELY_BUTTON;
  } else if (companyNanoId) {
    lifestylePagePath = TeamPath.companyNewLibraryLifestyle(
      companyNanoId,
      lifestyle.nanoId
    );
    lifestyleEditPagePath = TeamPath.editCompanyNewLibraryLifestyle(
      companyNanoId,
      lifestyle.nanoId
    );
    downloadButtonAction =
      LifestylesPermissions.READ_OTHER_COMPANY_LIBRARY_LIFESTYLE_DOWNLOAD_BUTTON;
    downloadSeparatelyButtonAction =
      LifestylesPermissions.READ_OTHER_COMPANY_LIBRARY_LIFESTYLE_DOWNLOAD_SEPARATELY_BUTTON;
  }

  return (
    <div className="w-120 max-w-full flex flex-col relative rounded-md group">
      <div className="group rounded-xl bg-gray-100 dark:bg-gray-800 border dark:border-gray-700 has-[:checked]:ring-8 has-[:checked]:border-blue-500 dark:has-[:checked]:border-blue-500 has-[:checked]:bg-blue-500/20 dark:has-[:checked]:bg-blue-500/20 overflow-hidden relative flex flex-col">
        <ThreeDStockLifestylesListItemInfo
          lifestyle={lifestyle}
          lifestylesSet={lifestylesSet}
          updateLifestylesSetCache={updateLifestylesSetCache}
          lifestylesSetCacheKeys={lifestylesSetCacheKeys}
          lifestylePagePath={lifestylePagePath}
        />

        <ThreeDStockLifestylesListItemPreview
          lifestyle={lifestyle}
          onLightboxOpen={handleOpenLightbox}
          downloadButtonAction={downloadButtonAction}
          downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
          updateLifestyleCache={updateLifestyleCache}
          onEditButtonMouseEnter={handleEditButtonMouseEnter}
          lifestyleEditPagePath={lifestyleEditPagePath}
          withRealAspectRatio={withRealAspectRatio}
          onLifestyleSimilarSearchFilter={onLifestyleSimilarSearchFilter}
          companyNanoId={companyNanoId}
          isMyLibrary={isMyLibrary}
        />
      </div>
    </div>
  );
}

export default memo<ThreeDStockLifestylesListItemProps>(
  ThreeDStockLifestylesListItem
);
